import React, { useState, useEffect } from 'react';
import './BiometricoTest.css';
import { getApiUrl } from '../../utils/api';

const BiometricoTest = () => {
    const [status, setStatus] = useState('disconnected');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [usuariosConHuella, setUsuariosConHuella] = useState([]);
    const [selectedUsuario, setSelectedUsuario] = useState('');
    const [devices, setDevices] = useState([]);
    const [logs, setLogs] = useState([]);

    //const API_URL = 'http://localhost/xampp4/htdocs/api_prestov2/reportes/funciones_php/api.php';
    const API_URL = getApiUrl();
    //

    useEffect(() => {
        checkConnection();
        loadUsuarios();
        loadUsuariosConHuella();
    }, []);

    const addLog = (message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        const newLog = {
            id: Date.now(),
            timestamp,
            message,
            type
        };
        setLogs(prev => [newLog, ...prev.slice(0, 19)]); // Mantener solo 20 logs
    };

    const apiRequest = async (tipo, data = {}) => {
        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ tipo, ...data })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            addLog(`Error en API: ${error.message}`, 'error');
            throw error;
        }
    };

    const checkConnection = async () => {
        setIsLoading(true);
        try {
            const result = await apiRequest('connectBiometric');
            if (result.success) {
                setStatus('connected');
                setMessage('✅ Servicio biométrico conectado');
                addLog('Servicio biométrico conectado correctamente', 'success');
                await loadDevices();
            } else {
                setStatus('disconnected');
                setMessage('❌ Servicio no disponible');
                addLog('Servicio biométrico no disponible', 'error');
            }
        } catch (error) {
            setStatus('error');
            setMessage('❌ Error de conexión');
            addLog(`Error de conexión: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const loadUsuarios = async () => {
        try {
            const result = await apiRequest('getUsuariosSinHuella');
            if (result.success) {
                setUsuarios(result.data.usuarios);
                addLog(`Cargados ${result.data.total} usuarios sin huella`, 'info');
            }
        } catch (error) {
            addLog('Error al cargar usuarios', 'error');
        }
    };

    const loadUsuariosConHuella = async () => {
        try {
            const result = await apiRequest('getUsuariosConHuella');
            if (result.success) {
                setUsuariosConHuella(result.data.usuarios);
                addLog(`Usuarios con huella: ${result.data.total}`, 'info');
            }
        } catch (error) {
            addLog('Error al cargar usuarios con huella', 'error');
        }
    };

    const loadDevices = async () => {
        try {
            const result = await apiRequest('listBiometricDevices');
            if (result.success) {
                setDevices(result.data.devices);
            }
        } catch (error) {
            addLog('Error al cargar dispositivos', 'error');
        }
    };

    const handleEnroll = async () => {
        if (!selectedUsuario) {
            setMessage('⚠️ Seleccione un usuario');
            return;
        }

        setIsLoading(true);
        setMessage('👆 Coloque su dedo en el lector...');
        addLog(`Iniciando enrollment para usuario ID: ${selectedUsuario}`, 'info');

        try {
            const result = await apiRequest('enrollBiometric', {
                idUsuario: parseInt(selectedUsuario)
            });

            if (result.success) {
                setMessage(`✅ Huella registrada: ${result.data.nombre}`);
                addLog(`Enrollment exitoso: ${result.data.nombre}`, 'success');
                await loadUsuarios();
                await loadUsuariosConHuella();
                setSelectedUsuario('');
            } else {
                setMessage(`❌ Error: ${result.message}`);
                addLog(`Error en enrollment: ${result.message}`, 'error');
            }
        } catch (error) {
            setMessage('❌ Error en enrollment');
            addLog(`Error en enrollment: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerify = async () => {
        setIsLoading(true);
        setMessage('🖼️ Abriendo ventana oficial de verificación...');
        addLog('Iniciando verificación con ventana oficial de DigitalPersona', 'info');

        try {
            const result = await apiRequest('verifyBiometric');
            
            addLog('Ventana de verificación abierta, esperando resultado...', 'info');

            if (result.success) {
                if (result.data && result.data.usuario) {
                    const usuario = result.data.usuario;
                    setMessage(`✅ Verificación exitosa: ${usuario.nombre}`);
                    addLog(`Usuario autenticado: ${usuario.nombre} (ID: ${usuario.idUsuario})`, 'success');
                    
                    // Actualizar última autenticación
                    await loadUsuariosConHuella();
                } else if (result.data && result.data.verified === true) {
                    setMessage(`✅ Huella verificada correctamente`);
                    addLog('Huella verificada exitosamente (sin datos de usuario)', 'success');
                } else {
                    setMessage('✅ Verificación completada');
                    addLog('Proceso de verificación completado', 'success');
                }
            } else {
                const errorMsg = result.message || 'Huella no reconocida';
                if (errorMsg.includes('no encontrada') || errorMsg.includes('no reconocida') || errorMsg.includes('no coincide')) {
                    setMessage('❌ Huella no encontrada en la base de datos');
                    addLog('Huella no coincide con ningún usuario registrado', 'warning');
                } else if (errorMsg.includes('cancelado') || errorMsg.includes('cerrado')) {
                    setMessage('⏹️ Verificación cancelada por el usuario');
                    addLog('Proceso de verificación cancelado', 'info');
                } else {
                    setMessage(`❌ Error en verificación: ${errorMsg}`);
                    addLog(`Error en verificación: ${errorMsg}`, 'error');
                }
            }
        } catch (error) {
            setMessage('❌ Error de conexión con el servicio');
            addLog(`Error de conexión: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteHuella = async (idUsuario, nombre) => {
        if (!window.confirm(`¿Eliminar huella de ${nombre}?`)) {
            return;
        }

        setIsLoading(true);
        addLog(`Eliminando huella de: ${nombre}`, 'info');

        try {
            const result = await apiRequest('deleteBiometric', { idUsuario });

            if (result.success) {
                setMessage(`✅ Huella eliminada: ${nombre}`);
                addLog(`Huella eliminada: ${nombre}`, 'success');
                await loadUsuarios();
                await loadUsuariosConHuella();
            } else {
                setMessage(`❌ Error: ${result.message}`);
                addLog(`Error eliminando huella: ${result.message}`, 'error');
            }
        } catch (error) {
            setMessage('❌ Error al eliminar');
            addLog(`Error eliminando huella: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = async () => {
        try {
            await apiRequest('cancelBiometric');
            setMessage('⏹️ Operación cancelada');
            addLog('Operación biométrica cancelada', 'info');
        } catch (error) {
            addLog('Error al cancelar operación', 'error');
        }
        setIsLoading(false);
    };

    const clearLogs = () => {
        setLogs([]);
        addLog('Logs limpiados', 'info');
    };

    return (
        <div className="biometrico-test">
            <div className="biometrico-header">
                <h1>🔒 DigitalPersona 4500 - Panel de Pruebas</h1>
                <div className={`status-indicator ${status}`}>
                    <span className="status-dot"></span>
                    {status === 'connected' ? 'Conectado' :
                        status === 'disconnected' ? 'Desconectado' : 'Error'}
                </div>
            </div>

            <div className="biometrico-content">
                {/* Panel de Control */}
                <div className="control-panel">
                    <h3>🎛️ Panel de Control</h3>

                    <div className="control-group">
                        <button
                            onClick={checkConnection}
                            disabled={isLoading}
                            className="btn btn-primary"
                        >
                            🔄 Verificar Conexión
                        </button>

                        <button
                            onClick={handleVerify}
                            disabled={isLoading || status !== 'connected'}
                            className="btn btn-verify"
                        >
                            {isLoading ? '🖼️ Esperando ventana...' : '👆 Verificar Huella'}
                        </button>

                        {isLoading && (
                            <button
                                onClick={handleCancel}
                                className="btn btn-cancel"
                            >
                                ⏹️ Cancelar
                            </button>
                        )}
                    </div>

                    <div className="message-display">
                        {message && (
                            <p className={`message ${message.includes('✅') ? 'success' :
                                message.includes('❌') ? 'error' :
                                    message.includes('⚠️') ? 'warning' : 'info'}`}>
                                {message}
                            </p>
                        )}
                    </div>
                    
                    <div className="verification-info">
                        <p className="info-text">
                            🔐 <strong>Proceso de Verificación:</strong><br/>
                            • Se abrirá la ventana oficial de DigitalPersona<br/>
                            • Coloque su dedo registrado en el lector<br/>
                            • El sistema comparará con las huellas en la base de datos<br/>
                            • Recibirá confirmación del usuario autenticado
                        </p>
                    </div>
                </div>

                {/* Panel de Enrollment */}
                <div className="enrollment-panel">
                    <h3>➕ Registrar Nueva Huella</h3>

                    <div className="enrollment-form">
                        <select
                            value={selectedUsuario}
                            onChange={(e) => setSelectedUsuario(e.target.value)}
                            disabled={isLoading}
                            className="user-select"
                        >
                            <option value="">Seleccionar usuario...</option>
                            {usuarios.map(usuario => (
                                <option key={usuario.idUsuario} value={usuario.idUsuario}>
                                    {usuario.nombre} ({usuario.usuario})
                                </option>
                            ))}
                        </select>

                        <button
                            onClick={handleEnroll}
                            disabled={isLoading || !selectedUsuario || status !== 'connected'}
                            className="btn btn-enroll"
                        >
                            {isLoading ? '👆 Registrando...' : '👆 Registrar Huella'}
                        </button>
                        
                        <div className="enrollment-info">
                            <p className="info-text">
                                📖 <strong>Proceso de Enrollment:</strong><br/>
                                • Se abrirá el programa oficial de DigitalPersona<br/>
                                • Use la interfaz oficial para capturar la huella<br/>
                                • Cierre el programa cuando termine<br/>
                                • El sistema guardará automáticamente la huella
                            </p>
                        </div>
                    </div>

                    <p className="usuarios-count">
                        📊 {usuarios.length} usuarios disponibles para registro
                    </p>
                </div>

                {/* Lista de Usuarios con Huella */}
                <div className="users-panel">
                    <h3>👥 Usuarios con Huella Registrada ({usuariosConHuella.length})</h3>

                    <div className="users-list">
                        {usuariosConHuella.length === 0 ? (
                            <p className="no-users">No hay usuarios con huella registrada</p>
                        ) : (
                            usuariosConHuella.map(usuario => (
                                <div key={usuario.idUsuario} className="user-card">
                                    <div className="user-info">
                                        <h4>{usuario.nombre}</h4>
                                        <p>@{usuario.usuario}</p>
                                        <small>
                                            Registrada: {new Date(usuario.fingerprint_enrolled_at).toLocaleDateString()}
                                        </small>
                                        {usuario.last_auth && (
                                            <small>
                                                Último acceso: {new Date(usuario.last_auth).toLocaleString()}
                                            </small>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleDeleteHuella(usuario.idUsuario, usuario.nombre)}
                                        disabled={isLoading}
                                        className="btn btn-danger btn-small"
                                    >
                                        🗑️ Eliminar
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Panel de Dispositivos */}
                {devices.length > 0 && (
                    <div className="devices-panel">
                        <h3>📱 Dispositivos Biométricos</h3>
                        {devices.map((device, index) => (
                            <div key={index} className="device-card">
                                <h4>{device.name}</h4>
                                <p>Fabricante: {device.manufacturer}</p>
                                <p>Modelo: {device.model}</p>
                                <p>Estado: <span className={`status ${device.status}`}>{device.status}</span></p>
                                <p>Conexión: {device.connection}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Panel de Logs */}
                <div className="logs-panel">
                    <div className="logs-header">
                        <h3>📋 Registro de Actividad</h3>
                        <button onClick={clearLogs} className="btn btn-small">
                            🗑️ Limpiar
                        </button>
                    </div>

                    <div className="logs-container">
                        {logs.length === 0 ? (
                            <p className="no-logs">No hay actividad reciente</p>
                        ) : (
                            logs.map(log => (
                                <div key={log.id} className={`log-entry ${log.type}`}>
                                    <span className="log-time">{log.timestamp}</span>
                                    <span className="log-message">{log.message}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BiometricoTest;